<template>
  <div id="inventory-goods">
    <div class="description">
      <van-field
        v-model="description"
        label="备注"
        placeholder="请输入备注"
        :readonly="!descEditing"
        :disabled="pageType === 'detail' || submitting || descSaving"
      >
        <template #button>
          <template v-if="pageType !== 'detail'">
            <span
              v-if="!descEditing"
              class="desc-action"
              :class="{ disabled: submitting }"
              @click="handleDescriptionEdit"
              >修改</span
            >
            <template v-else>
              <span
                class="desc-action muted"
                :class="{ disabled: submitting || descSaving }"
                @click="handleDescriptionCancel"
                >取消</span
              >
              <span
                class="desc-action"
                :class="{ disabled: submitting || descSaving }"
                @click="handleDescriptionSave"
                >{{ descSaving ? "保存中" : "保存" }}</span
              >
            </template>
          </template>
        </template>
      </van-field>
    </div>
    <div class="search-bar">
      <van-search
        v-model="condition"
        show-action
        placeholder="请输入商品名称/条码"
        @search="toSearch"
        @clear="clear"
      >
        <template #action>
          <div class="search-action" @click="toSearch">搜索</div>
          <van-button
            v-if="pageType !== 'detail'"
            type="primary"
            size="mini"
            :plain="!showDifferencesOnly"
            @click="toAdjust"
          >
            未盘点
          </van-button>
          <van-icon
            v-if="pageType !== 'detail'"
            name="scan"
            size="18"
            class="scan-icon"
            @click="toScan"
          />
        </template>
      </van-search>
    </div>

    <div class="listWrapper">
      <div class="leftWrapper">
        <div v-if="cateLoading" class="cate-loading">
          <van-loading size="20px" vertical>加载中</van-loading>
        </div>
        <MenuSideBar
          v-else
          ref="menuSidebar"
          :menuData="categories"
          :activeId="query.categoryId || 0"
          @toggleChildren="clickLeft"
        />
      </div>
      <div class="rightWrapper" ref="rightWrapper" @scroll="onRightScroll">
        <GlobalLoading :show="initialLoading" />
        <div v-if="displayProducts && displayProducts.length">
          <div
            v-for="(item, index) in displayProducts"
            :key="item.productId + '-' + index"
            class="product-card-wrap"
          >
            <van-tag
              v-if="isUntaken(item)"
              class="untaken-tag"
              type="warning"
              plain
              size="medium"
              >未盘点</van-tag>
            <van-tag
              v-else
              class="checked-tag"
              type="success"
              plain
              size="medium"
              >已盘点</van-tag>
            <van-card
              class="product-card"
              :desc="formatDesc(item)"
              :title="item.productName"
              :thumb="'https://fastly.jsdelivr.net/npm/@vant/assets/ipad.jpeg'"
            >
              <template #price>
                <div class="flex flex-between">
                  <span class="price">{{ formatePrice(item) }}</span>
                </div>
              </template>
              <template #footer>
                <div class="inventory-num">
                  <div class="stock-info">
                    <span class="stock-label">系统库存</span>
                    <span class="stock-value">{{ item.stockNum || 0 }}</span>
                  </div>
                  <div class="stock-info">
                    <span class="stock-label">盘点数</span>
                    <span
                      class="stock-value take"
                      :class="{ empty: isUntaken(item) }"
                      >{{ isUntaken(item) ? "--" : item.inventoryNum }}</span
                    >
                  </div>
                </div>
                <div class="action-links">
                  <span v-if="item.isWeigh" class="weight-badge">称</span>
                  <button
                    v-if="pageType !== 'detail'"
                    type="button"
                    class="action-btn primary"
                    @click.stop="handleAdjust(item)"
                  >
                    盘点
                  </button>
                  <button
                    v-if="pageType !== 'detail'"
                    type="button"
                    class="action-btn danger"
                    @click.stop="handleDelete(item)"
                  >
                    删除
                  </button>
                </div>
              </template>
            </van-card>
          </div>
          <div v-if="pageLoading && !initialLoading" class="list-tip">
            加载中...
          </div>
          <div v-else-if="finished" class="list-tip">已显示全部</div>
        </div>
        <div v-else-if="!initialLoading" class="emptyWrapper">暂无商品</div>
      </div>
    </div>

    <van-popup
      v-model="scanDialogVisible"
      position="center"
      class="scan-popup"
      round
      :close-on-click-overlay="false"
      get-container="body"
      @opened="onScanOpened"
      @closed="onScanClosed"
    >
      <div class="scan-dialog" @click="focusScanInput">
        <div class="scan-icon-wrapper">
          <van-icon name="scan" size="64px" color="#1989fa" />
          <p class="scan-tip">{{ scanTipText }}</p>
        </div>
        <input
          ref="scanInput"
          v-model="scanInputValue"
          class="scan-capture-input"
          type="text"
          inputmode="none"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          @input="onScanCaptureInput"
          @keyup.enter="onScanCaptureEnter"
          @blur="onScanInputBlur"
        />
        <div class="scan-actions">
          <van-button
            block
            round
            type="default"
            native-type="button"
            tabindex="-1"
            :disabled="scanProcessing"
            @mousedown.prevent
            @click.stop="closeScan"
            >取消</van-button
          >
        </div>
      </div>
    </van-popup>

    <div class="footer-actions">
      <div class="footer-btn-group">
        <template v-if="pageType !== 'detail'">
          <van-button
            round
            type="default"
            @click="handleAddProduct"
            :disabled="cateLoading || submitting"
            >添加商品</van-button
          >
          <van-button
            round
            type="primary"
            @click="handleApprove"
            :loading="submitting"
            :disabled="cateLoading || submitting"
            >审核通过</van-button
          >
        </template>
        <van-button round type="default" plain hairline @click="handleClose()"
          >关闭</van-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import GlobalLoading from "@/components/globalLoading";
import MenuSideBar from "@/components/menuSideBar/index";
import {
  getDetail,
  deleteProduct,
  updateDescription,
  approvePass,
} from "../../../service/api/stock/take/index";
import { reqWeightCategory } from "../../../service/api/product";

export default {
  name: "InventoryGoods",
  data() {
    return {
      pageType: "",
      sheetId: null,
      bizType: "",
      reasonId: "",
      description: "",
      savedDescription: "",
      descEditing: false,
      descSaving: false,
      categories: [{ id: 0, name: "全部" }],
      displayProducts: [],
      currentIndex: 0,
      query: {
        name: "",
        pageIndex: 1,
        pageSize: 20,
        available: true,
        categoryId: "",
        planId: null,
        scId: null,
      },
      condition: "",
      cateLoading: false,
      loading: false,
      pageLoading: false,
      initialLoading: false,
      finished: false,
      totalPage: 1,
      planId: null,
      planCode: "",
      scId: null,
      storeName: "",
      showDifferencesOnly: false,
      scanDialogVisible: false,
      scanInputValue: "",
      scanTipText: "请扫描商品条码",
      scanProcessing: false,
      scanFocusTimer: null,
      scanInputTimer: null,
      // 从扫码进入调整页后，返回时自动再开扫码（连扫）
      _resumeScanAfterAdjust: false,
      submitting: false,
      savedScrollTop: 0,
      _returnAction: null,
      _skipActivatedInit: false,
    };
  },
  components: {
    GlobalLoading,
    MenuSideBar,
  },
  created() {
    this.condition = "";
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const fromPath = from.path || "";
      vm._skipActivatedInit = true;
      if (fromPath.includes("/adjust")) {
        vm.pageType = to.query.type || vm.pageType || "";
        if (to.query.sheetId) {
          vm.sheetId = to.query.sheetId;
        }
        if (to.query.planId) {
          vm.planId = to.query.planId;
          vm.query.planId = to.query.planId;
        }
        vm.syncAfterAdjust();
        return;
      }
      if (fromPath.includes("/category")) {
        vm.pageType = to.query.type || vm.pageType || "";
        if (to.query.sheetId) {
          vm.sheetId = to.query.sheetId;
        }
        if (to.query.planId) {
          vm.planId = to.query.planId;
          vm.query.planId = to.query.planId;
        }
        vm.syncAfterCategory();
        return;
      }
      vm.init();
    });
  },
  beforeRouteLeave(to, from, next) {
    if ((to.path || "").includes("/adjust")) {
      this._returnAction = "adjust";
      if (this.$refs.rightWrapper) {
        this.savedScrollTop = this.$refs.rightWrapper.scrollTop || 0;
      }
    } else if (
      (to.path || "").includes("/category") ||
      to.name === "inventoryCategory"
    ) {
      this._resumeScanAfterAdjust = false;
      this._returnAction = "category";
    } else {
      this._resumeScanAfterAdjust = false;
      this._returnAction = null;
      this.savedScrollTop = 0;
    }
    next();
  },
  activated() {
    if (this._skipActivatedInit) {
      this._skipActivatedInit = false;
      return;
    }
    if (this._returnAction === "adjust") {
      this._returnAction = null;
      this.syncAfterAdjust();
      return;
    }
    if (this._returnAction === "category") {
      this._returnAction = null;
      this.syncAfterCategory();
      return;
    }
    this.init();
  },
  deactivated() {
    if (this.scanDialogVisible) {
      this.closeScan();
    }
    this.stopScanFocusWatch();
  },
  beforeDestroy() {
    this.stopScanFocusWatch();
    if (this.scanInputTimer) {
      clearTimeout(this.scanInputTimer);
      this.scanInputTimer = null;
    }
  },
  methods: {
    syncAfterAdjust() {
      if (!this.sheetId && this.$route.query.sheetId) {
        this.sheetId = this.$route.query.sheetId;
      }
      if (!this.planId && this.$route.query.planId) {
        this.planId = this.$route.query.planId;
        this.query.planId = this.planId;
      }

      const shouldResumeScan = this._resumeScanAfterAdjust;
      this._resumeScanAfterAdjust = false;

      const updatedProductId = this.$route.query.updatedProductId;
      const updatedAdjustNum = this.$route.query.updatedAdjustNum;
      const adjustSuccess =
        updatedProductId != null && updatedProductId !== "";
      if (adjustSuccess) {
        this.applyProductAdjustNum(updatedProductId, updatedAdjustNum);
        this.clearUpdatedQuery();
      }

      if (!this.displayProducts.length) {
        this.loadProductPage(false);
        if (shouldResumeScan && adjustSuccess) {
          this.resumeScanAfterAdjust();
        }
        return;
      }

      this.$nextTick(() => {
        if (this.$refs.rightWrapper) {
          this.$refs.rightWrapper.scrollTop = this.savedScrollTop || 0;
        }
      });

      if (shouldResumeScan && adjustSuccess) {
        this.resumeScanAfterAdjust();
      }
    },

    // 盘点调整返回后恢复扫码弹窗，避开 toast 抢焦点
    resumeScanAfterAdjust() {
      if (this.pageType === "detail") {
        return;
      }
      try {
        this.$toast.clear();
      } catch (e) {
        // ignore
      }
      this.$nextTick(() => {
        setTimeout(() => {
          this.toScan();
        }, 80);
      });
    },

    syncAfterCategory() {
      if (!this.sheetId && this.$route.query.sheetId) {
        this.sheetId = this.$route.query.sheetId;
      }
      if (!this.planId && this.$route.query.planId) {
        this.planId = this.$route.query.planId;
        this.query.planId = this.planId;
      }
      this.loadCategories();
      this.loadProductPage(false);
    },

    clearUpdatedQuery() {
      const {
        updatedProductId,
        updatedAdjustNum,
        updatedTakeNum,
        fromCategory,
        ...rest
      } = this.$route.query || {};
      this.$router.replace({
        path: this.$route.path,
        query: rest,
      });
    },

    applyProductAdjustNum(productId, adjustNum) {
      this.displayProducts.forEach((item) => {
        if (String(item.productId) === String(productId)) {
          const next = this.$utils.preciseAdd(
            Number(item.inventoryNum || 0),
            Number(adjustNum || 0),
          );
          item.inventoryNum = next;
          item.takeNum = next;
        }
      });
    },

    init() {
      this.pageType = this.$route.query.type || "";
      this.sheetId = this.$route.query.sheetId || null;
      this.planId = this.$route.query.planId || null;
      this.query.planId = this.planId;

      if (!this.sheetId) {
        this.$toast("缺少盘点单参数");
        return;
      }

      this.resetPageState();
      this.loadCategories();
      this.loadProductPage(false);
    },

    async loadCategories() {
      this.cateLoading = true;
      try {
        const res = await reqWeightCategory();
        if (res.code !== 200) {
          return;
        }
        const data = res.data || [];
        const firstLevel = data
          .filter((item) => !item.parentId)
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));
        this.categories = [{ id: 0, name: "全部" }].concat(firstLevel);
      } catch (e) {
        console.warn("加载类目失败", e);
        this.$toast("加载类目失败");
      } finally {
        this.cateLoading = false;
      }
    },

    resetPageState() {
      this.displayProducts = [];
      this.categories = [{ id: 0, name: "全部" }];
      this.description = "";
      this.savedDescription = "";
      this.descEditing = false;
      this.descSaving = false;
      this.condition = "";
      this.query.categoryId = "";
      this.query.name = "";
      this.query.pageIndex = 1;
      this.finished = false;
      this.showDifferencesOnly = false;
      this.loading = false;
      this.cateLoading = false;
      this.initialLoading = false;
      this.pageLoading = false;
      this.savedScrollTop = 0;
    },

    resolveInventoryNum(item) {
      if (!item) {
        return "";
      }
      if (item.inventoryNum === 0 || item.inventoryNum) {
        return item.inventoryNum;
      }
      if (item.takeNum === 0 || item.takeNum) {
        return item.takeNum;
      }
      return "";
    },

    normalizeProduct(item) {
      const productId = item.productId || item.id;
      const inventoryNum = this.resolveInventoryNum(item);
      return {
        ...item,
        productId,
        inventoryNum,
      };
    },

    async loadProductPage(isLoadMore = false) {
      if (!this.sheetId) {
        this.finished = true;
        this.loading = false;
        this.cateLoading = false;
        this.initialLoading = false;
        this.pageLoading = false;
        return;
      }
      if (this.pageLoading) {
        return;
      }

      if (isLoadMore) {
        if (this.finished) {
          return;
        }
        this.query.pageIndex += 1;
      } else {
        this.query.pageIndex = 1;
        this.finished = false;
        this.displayProducts = [];
        this.initialLoading = true;
        if (this.$refs.rightWrapper) {
          this.$refs.rightWrapper.scrollTop = 0;
        }
      }

      this.pageLoading = true;
      this.loading = true;
      try {
        const params = {
          id: this.sheetId,
          pageIndex: this.query.pageIndex,
          pageSize: this.query.pageSize,
        };
        if (this.query.categoryId) {
          params.categoryId = this.query.categoryId;
        }
        // 以输入框当前内容为准，避免删了字但未点搜索时仍带旧 keyword
        const keyword = (this.condition || "").trim();
        this.query.name = keyword;
        if (keyword) {
          params.keyword = keyword;
        }
        if (this.showDifferencesOnly) {
          params.unTake = true;
        }

        const res = await getDetail(params);
        if (res.code !== 200 || !res.data) {
          this.finished = true;
          return;
        }

        const data = res.data;
        if (data.planCode) this.planCode = data.planCode;
        if (data.planId) {
          this.planId = data.planId;
          this.query.planId = data.planId;
        }
        if (data.scName) this.storeName = data.scName;
        if (data.scId) this.scId = data.scId;
        if (data.description != null && !isLoadMore) {
          this.description = data.description || "";
          this.savedDescription = this.description;
        }

        const page = data.details || {};
        const list = Array.isArray(page)
          ? page
          : page.datas || page.list || [];
        let totalPage = Array.isArray(page) ? null : page.totalPage;
        if (!totalPage) {
          const totalCount = Array.isArray(page)
            ? null
            : page.totalCount != null
              ? page.totalCount
              : page.total;
          if (totalCount != null) {
            totalPage =
              Math.ceil(Number(totalCount) / this.query.pageSize) || 1;
          }
        }
        this.totalPage = totalPage || 1;

        const pageProducts = (list || []).map((item) =>
          this.normalizeProduct(item),
        );

        if (isLoadMore) {
          this.displayProducts = this.displayProducts.concat(pageProducts);
        } else {
          this.displayProducts = pageProducts;
        }

        if (totalPage) {
          this.finished =
            this.query.pageIndex >= totalPage || list.length === 0;
        } else {
          this.finished =
            list.length === 0 || list.length < this.query.pageSize;
        }
      } catch (e) {
        console.warn("分页加载盘点单明细失败", e);
        this.finished = true;
        if (!isLoadMore) {
          this.displayProducts = [];
        }
      } finally {
        this.pageLoading = false;
        this.loading = false;
        this.cateLoading = false;
        this.initialLoading = false;
      }
    },

    onRightScroll(e) {
      const el = e && e.target;
      if (!el || this.pageLoading || this.finished || this.initialLoading) {
        return;
      }
      const reachBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
      if (reachBottom) {
        this.loadProductPage(true);
      }
    },

    clickLeft(id) {
      if (id === this.query.categoryId || (id === 0 && !this.query.categoryId)) {
        return;
      }
      this.query.categoryId = id === 0 ? "" : id;
      this.loadProductPage(false);
    },

    formatDesc(item) {
      return `商品条码：${item.externalCode || "--"}`;
    },
    formatePrice(item) {
      return "￥" + item.retailPrice + (item.unit ? `/${item.unit}` : "");
    },
    isUntaken(item) {
      return !item || item.inventoryNum === "" || item.inventoryNum == null;
    },

    toSearch() {
      this.loadProductPage(false);
    },
    clear() {
      this.condition = "";
      this.query.name = "";
      this.loadProductPage(false);
    },

    handleAdjust(item) {
      const query = {
        planId: this.planId,
        productId: item.productId,
        sheetId: this.sheetId,
        productName: item.productName || "",
        productCode: item.productCode || "",
        externalCode: item.externalCode || "",
        stockNum: item.stockNum || 0,
        inventoryNum:
          item.inventoryNum === 0 || item.inventoryNum
            ? item.inventoryNum
            : "",
        takeNum:
          item.takeNum === 0 || item.takeNum ? item.takeNum : item.inventoryNum,
        isWeigh: item.isWeigh ? 1 : 0,
        retailPrice: item.retailPrice || 0,
        unit: item.unit || "",
      };
      if (this.pageType) {
        query.type = this.pageType;
      }
      this.$router.push({
        path: "/stock/inventory/adjust",
        query,
      });
    },

    handleDelete(item) {
      this.$dialog
        .confirm({
          title: "确认删除",
          message: `确定要删除商品【${item.productName}】吗？`,
        })
        .then(async () => {
          if (!this.sheetId || !item.productId) {
            this.$toast("缺少删除参数");
            return;
          }
          this.$toast.loading({
            message: "删除中...",
            forbidClick: true,
            duration: 0,
          });
          try {
            const res = await deleteProduct({
              sheetId: this.sheetId,
              productId: item.productId,
            });
            this.$toast.clear();
            if (res.code !== 200) {
              this.$nextTick(() => {
                this.$toast(res.msg || "删除失败");
              });
              return;
            }
            this.displayProducts = this.displayProducts.filter(
              (product) =>
                String(product.productId) !== String(item.productId),
            );
            this.$nextTick(() => {
              this.$toast("删除成功");
            });
          } catch (e) {
            this.$toast.clear();
            this.$nextTick(() => {
              this.$toast(this.getErrorMsg(e, "删除失败，请重试"));
            });
            console.warn("删除盘点商品失败", e);
          }
        })
        .catch(() => {});
    },

    handleDescriptionEdit() {
      if (this.pageType === "detail" || this.submitting) {
        return;
      }
      this.descEditing = true;
    },

    handleDescriptionCancel() {
      this.description = this.savedDescription || "";
      this.descEditing = false;
    },

    async handleDescriptionSave() {
      if (this.pageType === "detail" || !this.sheetId || this.descSaving) {
        return;
      }
      const nextDesc = this.description || "";
      if (nextDesc === (this.savedDescription || "")) {
        this.descEditing = false;
        this.$toast("备注未修改");
        return;
      }
      this.descSaving = true;
      try {
        const res = await updateDescription({
          sheetId: this.sheetId,
          description: nextDesc,
        });
        if (res.code !== 200) {
          this.$toast(res.msg || "备注保存失败");
          return;
        }
        this.savedDescription = nextDesc;
        this.descEditing = false;
        this.$toast("备注已保存");
      } catch (e) {
        console.warn("修改备注失败", e);
        this.$toast(this.getErrorMsg(e, "备注保存失败，请重试"));
      } finally {
        this.descSaving = false;
      }
    },

    toAdjust() {
      this.showDifferencesOnly = !this.showDifferencesOnly;
      this.loadProductPage(false);
    },

    toScan() {
      this.scanInputValue = "";
      this.scanTipText = "请扫描商品条码";
      this.scanProcessing = false;
      this.scanDialogVisible = true;
    },

    onScanOpened() {
      this.resetScanCapture();
      this.$nextTick(() => {
        this.focusScanInput(true);
        this.startScanFocusWatch();
      });
    },

    onScanClosed() {
      this.stopScanFocusWatch();
      if (this.scanInputTimer) {
        clearTimeout(this.scanInputTimer);
        this.scanInputTimer = null;
      }
      this.scanInputValue = "";
      this.scanTipText = "请扫描商品条码";
      this.scanProcessing = false;
    },

    closeScan() {
      this.scanDialogVisible = false;
    },

    resetScanCapture() {
      this.scanInputValue = "";
      if (this.$refs.scanInput) {
        this.$refs.scanInput.value = "";
      }
    },

    focusScanInput(force) {
      if (!this.scanDialogVisible) {
        return;
      }
      if (this.scanProcessing && !force) {
        return;
      }
      const el = this.$refs.scanInput;
      if (!el) {
        return;
      }
      try {
        el.focus({ preventScroll: true });
        if (typeof el.select === "function") {
          el.select();
        }
      } catch (e) {
        el.focus();
      }
    },

    onScanInputBlur() {
      if (!this.scanDialogVisible) {
        return;
      }
      setTimeout(() => {
        this.focusScanInput(true);
      }, 50);
    },

    startScanFocusWatch() {
      this.stopScanFocusWatch();
      this.scanFocusTimer = setInterval(() => {
        if (!this.scanDialogVisible) {
          return;
        }
        const el = this.$refs.scanInput;
        if (el && document.activeElement !== el) {
          this.focusScanInput(true);
        }
      }, 300);
    },

    stopScanFocusWatch() {
      if (this.scanFocusTimer) {
        clearInterval(this.scanFocusTimer);
        this.scanFocusTimer = null;
      }
    },

    onScanCaptureInput() {
      if (this.scanProcessing) {
        return;
      }
      const code = (this.scanInputValue || "").trim();
      if (this.scanInputTimer) {
        clearTimeout(this.scanInputTimer);
      }
      if (code.length >= 4 && code.length <= 32) {
        this.scanInputTimer = setTimeout(() => {
          this.processScannedCode(code);
        }, 120);
      }
    },

    onScanCaptureEnter() {
      if (this.scanProcessing) {
        return;
      }
      if (this.scanInputTimer) {
        clearTimeout(this.scanInputTimer);
        this.scanInputTimer = null;
      }
      const code = (this.scanInputValue || "").trim();
      if (!code) {
        return;
      }
      if (code.length < 4 || code.length > 32) {
        this.scanTipText = "条码有误，请重新扫描";
        this.resetScanCapture();
        this.focusScanInput(true);
        return;
      }
      this.processScannedCode(code);
    },

    recoverScan(message) {
      this.scanProcessing = false;
      this.scanTipText = message || "请扫描商品条码";
      this.resetScanCapture();
      this.$nextTick(() => {
        this.focusScanInput(true);
        setTimeout(() => this.focusScanInput(true), 200);
        setTimeout(() => this.focusScanInput(true), 500);
      });
    },

    async processScannedCode(code) {
      if (!code || !this.sheetId) {
        this.recoverScan("扫码参数错误，请重试");
        return;
      }
      if (this.scanProcessing) {
        return;
      }
      this.scanProcessing = true;
      this.scanTipText = "查询中...";
      this.resetScanCapture();
      try {
        const res = await getDetail({
          id: this.sheetId,
          pageIndex: 1,
          pageSize: 10,
          externalCode: code,
        });

        if (res.code !== 200 || !res.data) {
          this.recoverScan("查询失败，请重新扫描");
          return;
        }

        const page = res.data.details || {};
        const list = Array.isArray(page)
          ? page
          : page.datas || page.list || [];
        if (!list.length) {
          this.recoverScan("该商品不在盘点单中，请继续扫码");
          return;
        }

        let product =
          list.find(
            (item) =>
              item.skuCode === code ||
              item.externalCode === code ||
              item.productCode === code,
          ) || list[0];

        product = this.normalizeProduct(product);
        // 标记从扫码进入调整，返回后自动再开扫码实现连扫
        this._resumeScanAfterAdjust = true;
        this.closeScan();
        this.handleAdjust(product);
      } catch (e) {
        console.warn("扫码查询失败", e);
        this.recoverScan(
          this.getErrorMsg(e, "查询失败，请重新扫描"),
        );
      }
    },

    handleAddProduct() {
      const query = this.$route.query;
      this.$router.replace({
        name: "inventoryCategory",
        query: {
          ...query,
          planId: this.planId,
          sheetId: this.sheetId,
        },
      });
    },

    handleApprove() {
      this.$dialog
        .confirm({
          title: "确认审核",
          message: "确认审核通过该盘点单吗？",
        })
        .then(() => {
          this.doApprove();
        })
        .catch(() => {});
    },

    doApprove() {
      if (!this.sheetId) {
        this.$toast("缺少盘点单参数");
        return;
      }
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      this.$toast.loading({
        message: "审核中...",
        forbidClick: true,
        duration: 0,
      });
      approvePass({ id: this.sheetId })
        .then((approveRes) => {
          this.$toast.clear();
          this.submitting = false;
          if (approveRes.code === 200) {
            this.$nextTick(() => {
              this.$toast("审核通过");
            });
            this.handleClose(true);
          } else {
            this.$nextTick(() => {
              this.$toast(approveRes.msg || "审核通过失败，请重试");
            });
          }
        })
        .catch((e) => {
          console.error("审核通过失败:", e);
          this.$toast.clear();
          this.submitting = false;
          this.$nextTick(() => {
            this.$toast(this.getErrorMsg(e, "审核通过失败，请重试"));
          });
        });
    },

    getErrorMsg(e, fallback) {
      return (
        (e && e.msg) ||
        (e && e.response && e.response.data && e.response.data.msg) ||
        fallback ||
        "操作失败，请重试"
      );
    },

    handleClose(force = false) {
      this.resetPageState();
      this.$router.push("/stock/inventory");
    },
  },
};
</script>

<style lang="less" scoped>
* {
  touch-action: pan-y;
}

#inventory-goods {
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f5f6f8;
}

.description {
  background: #fff;
  border-bottom: 1px solid #f0f1f3;

  .van-cell {
    padding: 10px 12px 10px 16px;
    align-items: center;

    ::v-deep .van-field__label {
      width: 2.5rem !important;
      color: #646566;
      margin-right: 8px;
    }

    ::v-deep .van-field__control {
      color: #323233;
    }

    ::v-deep .van-field__body {
      border: none;
      background: #f7f8fa;
      border-radius: 6px;
      padding: 4px 8px;
      min-height: 28px;
    }

    ::v-deep .van-field__button {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-left: 8px;
      padding-left: 0;
    }
  }

  .desc-action {
    font-size: 14px;
    color: #1989fa;
    line-height: 28px;
    white-space: nowrap;
    cursor: pointer;

    &.muted {
      color: #969799;
    }

    &.disabled {
      opacity: 0.45;
      pointer-events: none;
    }
  }
}

.search-bar {
  background: #fff;
  border-bottom: 1px solid #f0f1f3;

  ::v-deep .van-search {
    display: flex;
    align-items: center;
    padding: 8px 12px;
  }

  ::v-deep .van-search__content {
    flex: 1;
    min-width: 0;
    background: #f7f8fa;
    border-radius: 8px;
  }

  ::v-deep .van-search__action {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 0 8px;
    line-height: 1;
    flex: 0 0 auto;
  }

  .search-action {
    font-size: 13px;
    color: #1989fa;
    white-space: nowrap;
    line-height: 28px;
    height: 28px;
  }

  ::v-deep .van-button--mini {
    height: 28px;
    min-width: auto;
    padding: 0 10px;
    border-radius: 14px;
    font-size: 12px;
    line-height: 26px;
  }

  .scan-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: #646566;
    vertical-align: middle;
  }
}

::v-deep .van-card__price {
  width: 100%;
}

.listWrapper {
  display: flex;
  flex: 1;
  overflow: hidden;

  .leftWrapper {
    width: 5rem;
    overflow-y: auto;
    background: #f7f8fa;
    position: relative;

    .cate-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 120px;
      padding: 16px 0;
      color: #969799;
      font-size: 12px;
    }
  }

  .rightWrapper {
    flex: 1;
    overflow-y: auto;
    padding: 8px 8px 120px;
    position: relative;

    .list-tip {
      text-align: center;
      padding: 10px 0 16px;
      color: #c8c9cc;
      font-size: 12px;
      letter-spacing: 0.2px;
    }

    .emptyWrapper {
      text-align: center;
      padding-top: 64px;
      color: #c8c9cc;
      font-size: 13px;
    }
  }
}

.product-card-wrap {
  position: relative;
  margin: 0 0 8px;
}

.untaken-tag, .checked-tag {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  border-radius: 0 8px 0 0;
  font-size: 11px;
  line-height: 1.2;
  padding: 2px 6px;
}

.product-card {
  padding: 0;
  margin: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);

  ::v-deep .van-card__header {
    padding: 10px 10px 6px;
  }

  ::v-deep .van-card__thumb {
    width: 72px;
    height: 72px;
    margin-right: 10px;
  }

  ::v-deep .van-card__content {
    min-height: 56px;
    display: flex !important;
    justify-content: center !important;
  }

  ::v-deep .van-card__title {
    font-size: 12px;
    font-weight: 600;
    color: #1f2329;
    line-height: 1.35;
    letter-spacing: 0.1px;
    // padding-right: 44px;
  }

  ::v-deep .van-card__desc {
    margin-top: 3px;
    color: #969799;
    font-size: 11px;
    line-height: 1.4;
  }

  ::v-deep .van-card__footer {
    padding: 0 10px 10px;
  }
}

.price {
  color: #ee0a24;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
}

.inventory-num {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin: 2px 0 8px;
  padding: 6px 8px;
  background: #f7f8fa;
  border-radius: 6px;

  .stock-info {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;

    .stock-label {
      font-size: 11px;
      color: #969799;
      letter-spacing: 0.2px;
    }

    .stock-value {
      font-size: 13px;
      font-weight: 600;
      color: #323233;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.2px;

      &.take {
        color: #1989fa;
      }

      &.empty {
        color: #c8c9cc;
        font-weight: 500;
      }
    }
  }
}

.action-links {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.action-btn {
  appearance: none;
  border: 1px solid #ebedf0;
  background: #fff;
  color: #323233;
  font-size: 12px;
  line-height: 1;
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  cursor: pointer;

  &.primary {
    color: #1989fa;
    border-color: rgba(25, 137, 250, 0.35);
    background: rgba(25, 137, 250, 0.06);
  }

  &.danger {
    color: #ee0a24;
    border-color: rgba(238, 10, 36, 0.28);
    background: rgba(238, 10, 36, 0.05);
  }

  &:active {
    opacity: 0.78;
  }
}

.weight-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: auto;
  width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: #ff7a1a;
  border-radius: 50%;
}

.scan-popup {
  width: 78vw;
  max-width: 320px;
  overflow: hidden;
}

.scan-dialog {
  position: relative;
  padding: 28px 20px 20px;
  text-align: center;
}

.scan-capture-input {
  position: absolute;
  left: 12px;
  right: 12px;
  top: 12px;
  height: 28px;
  opacity: 0.02;
  border: 0;
  padding: 0;
  margin: 0;
  color: transparent;
  background: transparent;
  caret-color: transparent;
}

.scan-icon-wrapper {
  margin-bottom: 8px;
}

.scan-tip {
  margin-top: 10px;
  font-size: 15px;
  color: #646566;
  min-height: 22px;
}

.scan-actions {
  margin-top: 22px;
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  border-top: 1px solid #ebedf0;
  z-index: 100;
}

.footer-btn-group {
  display: flex;
  gap: 8px;

  .van-button {
    flex: 1;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
  }
}

</style>
